// ── 급여명세서 인쇄 유틸리티 (근로기준법 시행령 제27조의2 준수) ─────────────────
// 법적 필수 기재사항: 성명·생년월일·임금지급일·근로일수·총 근로시간·
// 구성항목별 금액·변동항목 계산방법(산출식)·공제항목별 금액+총액
// 4대보험·소득세는 관련 법률에서 요율을 규정하므로 산출식 불요

import { apiClient } from '../../../api/apiClient';

// ── 숫자 포맷 ──────────────────────────────────────────────────────────────
const fmt = (n) => n != null ? Number(n).toLocaleString('ko-KR') : '0';
const floor10 = (n) => Math.floor(n / 10) * 10;

// ── 통상임금 기반 시급·일급 계산 (DB에 저장된 월소정근로시간 기반) ────────────
const calcHourly = (ow, mh) => mh ? Math.floor(ow / mh) : 0;
const calcDaily = (ow, mh, dah) => floor10(calcHourly(ow, mh) * (dah || 0));
const hasNonZeroAmount = (item) => Number(item?.amount) !== 0;
const isDeductionRuleEnabled = (rules, key) => rules?.[key]?.enabled === true;
const workDaysWithPaidLeave = (record) => {
  const paid = Number(record?.paid_leave_days) || 0;
  const work = record?.work_days ?? 0;
  return paid > 0 ? `${work}일 (유급 ${paid}일)` : `${work}일`;
};

// ── 산출식 생성 (근태공제·주휴수당공제 — 법적 필수) ─────────────────────────
// deduction_rules 비활성 항목은 payroll_records에 0으로 저장되어 자동 제외됨
function buildFormulas(rec) {
  const ow = Number(rec.ordinary_wage) || 0;
  const mh = Number(rec.monthly_hours) || 0;
  const dah = Number(rec.daily_avg_hours) || 0;
  if (ow === 0 || !mh) return [];
  const hourly = calcHourly(ow, mh);
  const daily = calcDaily(ow, mh, dah);
  const formulas = [];
  const rules = rec.deduction_rules || {};

  // 근태공제 산출식 (차감규칙 비활성 항목은 DB에 0 저장 → 자동 제외)
  const absent = isDeductionRuleEnabled(rules, 'absent') ? Number(rec.absent_days) || 0 : 0;
  const lateMins = isDeductionRuleEnabled(rules, 'late') ? Number(rec.late_minutes) || 0 : 0;
  const earlyMins = isDeductionRuleEnabled(rules, 'early_leave') ? Number(rec.early_leave_minutes) || 0 : 0;
  const unpaid = isDeductionRuleEnabled(rules, 'unpaid_leave') ? Number(rec.unpaid_leave_days) || 0 : 0;
  if (absent > 0 || lateMins > 0 || earlyMins > 0 || unpaid > 0) {
    const parts = [];
    if (absent > 0) parts.push(`일급(${fmt(daily)}) &times; 결근(${absent}일) = ${fmt(daily * absent)}`);
    if (lateMins > 0) parts.push(`시급(${fmt(hourly)}) &times; 지각(${lateMins}분&divide;60) = ${fmt(floor10(hourly * lateMins / 60))}`);
    if (earlyMins > 0) parts.push(`시급(${fmt(hourly)}) &times; 조퇴(${earlyMins}분&divide;60) = ${fmt(floor10(hourly * earlyMins / 60))}`);
    if (unpaid > 0) parts.push(`일급(${fmt(daily)}) &times; 무급(${unpaid}일) = ${fmt(daily * unpaid)}`);
    formulas.push({ name: '근태공제', parts });
  }

  // 주휴수당공제 산출식
  const whWeeks = Number(rec.weekly_holiday_weeks) || 0;
  const whDed = Number(rec.weekly_holiday_deduction) || 0;
  if (whWeeks > 0 || whDed > 0) {
    formulas.push({ name: '주휴수당공제', parts: [`일급(${fmt(daily)}) &times; 미개근(${whWeeks}주) = ${fmt(whDed)}`] });
  }
  return formulas;
}

/**
 * 급여명세서 HTML 생성 (근로기준법 시행령 제27조의2 준수)
 */
function buildPayslipHTML(record, period, companyName = '(주)파인그룹') {
  const earnings = record.items.filter((i) => i.item_type === 'earning' && hasNonZeroAmount(i));
  const deductions = record.items.filter((i) => i.item_type === 'deduction' && hasNonZeroAmount(i));
  const maxRows = Math.max(earnings.length, deductions.length);

  let totE = 0, totD = 0, taxFreeTotal = 0;
  earnings.forEach((e) => { totE += Number(e.amount); taxFreeTotal += Number(e.tax_free_amount || 0); });
  deductions.forEach((d) => { totD += Number(d.amount); });

  const ow = Number(record.ordinary_wage) || 0;
  const mh = Number(record.monthly_hours) || 0;
  const dah = Number(record.daily_avg_hours) || 0;
  const hourly = calcHourly(ow, mh);
  const daily = calcDaily(ow, mh, dah);

  const workMins = Number(record.total_work_minutes) || 0;
  const workH = Math.floor(workMins / 60);
  const workM = workMins % 60;

  const pStart = period.period_start ? String(period.period_start).slice(0, 10) : '';
  const pEnd = period.period_end ? String(period.period_end).slice(0, 10) : '';
  const payDate = period.pay_date ? String(period.pay_date).slice(0, 10) : '';
  const birthDate = record.birth_date ? String(record.birth_date).slice(0, 10) : '-';

  const formulas = buildFormulas(record);
  const rules = record.deduction_rules || {};
  const hasAttendance = Boolean(record.attendance_time_type);
  const statItems = [
    { label: '소정근로일', value: hasAttendance ? `${record.biz_days || 0}일` : '해당없음' },
    { label: '출근일수(유급휴가)', value: hasAttendance ? workDaysWithPaidLeave(record) : '해당없음' },
    ...(isDeductionRuleEnabled(rules, 'absent') ? [
      { label: '결근', value: hasAttendance ? `${record.absent_days}일` : '-' },
    ] : []),
    { label: '총 근로시간', value: hasAttendance ? `${workH}시간${workM > 0 ? ` ${workM}분` : ''}` : '-' },
    ...(isDeductionRuleEnabled(rules, 'late') ? [
      { label: '지각', value: `${record.late_count}회${record.late_minutes > 0 ? ` (${record.late_minutes}분)` : ''}` },
    ] : []),
    ...(isDeductionRuleEnabled(rules, 'early_leave') ? [
      { label: '조퇴', value: `${record.early_leave_count}회${record.early_leave_minutes > 0 ? ` (${record.early_leave_minutes}분)` : ''}` },
    ] : []),
    ...(isDeductionRuleEnabled(rules, 'unpaid_leave') ? [
      { label: '무급휴가', value: `${record.unpaid_leave_days}일` },
    ] : []),
    { label: '주휴공제', value: record.weekly_holiday_weeks > 0 ? `${record.weekly_holiday_weeks}주` : '-' },
  ];
  const statsHtml = statItems.map((item) => `<dl><dt>${item.label}</dt><dd>${item.value}</dd></dl>`).join('');

  // 지급/공제 테이블 행
  let tableRows = '';
  for (let i = 0; i < maxRows; i++) {
    const e = earnings[i];
    const d = deductions[i];
    const tf = e && Number(e.tax_free_amount) > 0 ? `<span class="tf">${fmt(e.tax_free_amount)}</span>` : '';
    const eClass = e && Number(e.amount) < 0 ? 'r neg' : 'r';
    tableRows += `<tr>
      <td>${e ? e.item_name : ''}</td>
      <td class="${eClass}">${e ? `${tf}${fmt(e.amount)}` : ''}</td>
      <td>${d ? d.item_name : ''}</td>
      <td class="r">${d ? fmt(d.amount) : ''}</td>
    </tr>`;
  }

  return `
    <article class="slip">
      <!-- 상단 액센트 라인 -->
      <div class="accent-bar"></div>

      <!-- 헤더 -->
      <header>
        <div class="co">${companyName}</div>
        <h1>급여명세서</h1>
        <p class="period">${period.year}년 ${period.month}월&ensp;|&ensp;${pStart} - ${pEnd}</p>
      </header>

      <!-- 직원 정보 (법적 필수: 성명, 생년월일, 임금지급일) -->
      <table class="info">
        <tr><th>성명</th><td>${record.user_name}</td><th>부서</th><td>${record.department || '-'}</td></tr>
        <tr><th>생년월일</th><td>${birthDate}</td><th>임금지급일</th><td>${payDate}</td></tr>
      </table>

      <!-- 근무 정보 (법적 필수: 근로일수, 총 근로시간수) -->
      <h2>근무 정보</h2>
      <div class="stats">
        ${statsHtml}
      </div>

      <!-- 통상임금 (공제 산출 기준) -->
      ${ow > 0 ? `
      <div class="wage">
        <span class="wage-lbl">통상임금</span>
        <span class="wage-val">${fmt(ow)}원</span>
        ${mh ? `<span class="wage-rate">시급 ${fmt(hourly)}원&ensp;/&ensp;일급 ${fmt(daily)}원</span>` : ''}
      </div>` : ''}

      <!-- 지급/공제 항목 (법적 필수: 구성항목별 금액) -->
      <table class="pay">
        <colgroup><col style="width:28%"><col style="width:22%"><col style="width:28%"><col style="width:22%"></colgroup>
        <thead>
          <tr class="cat"><th colspan="2" class="earn">지급 항목</th><th colspan="2" class="ded">공제 항목</th></tr>
          <tr class="sub"><th>항목</th><th class="r">금액</th><th>항목</th><th class="r">금액</th></tr>
        </thead>
        <tbody>${tableRows}</tbody>
        <tfoot>
          <tr><td>합계</td><td class="r earn-t">${fmt(totE)}</td><td>합계</td><td class="r ded-t">${fmt(totD)}</td></tr>
        </tfoot>
      </table>

      <!-- 변동 항목 산출 내역 (법적 필수: 계산방법) -->
      ${formulas.length > 0 ? `
      <div class="formula">
        <h2>산출 내역</h2>
        ${formulas.map((f) => `
          <div class="f-block">
            <span class="f-name">${f.name}</span>
            ${f.parts.map((p) => `<span class="f-expr">${p}</span>`).join('')}
          </div>
        `).join('')}
      </div>` : ''}

      <!-- 합계 요약 (법적 필수: 임금 총액) -->
      <div class="summary">
        <div class="summary-top">
          <span>과세소득 <b>${fmt(totE - taxFreeTotal)}</b>원</span>
          <span>비과세 <b>${fmt(taxFreeTotal)}</b>원</span>
        </div>
        <div class="summary-net">
          <span>실수령액</span>
          <span class="net">${fmt(totE - totD)}원</span>
        </div>
      </div>

      <footer>본 급여명세서는 근로기준법 시행령 제27조의2에 따라 교부합니다.</footer>
    </article>`;
}

// ── 개별 급여명세서 인쇄 ──────────────────────────────────────────────────────
export async function printPayslip(recordId, period, companyName) {
  const res = await apiClient.get(`/api/admin/payroll/monthly/records/${recordId}`);
  if (!res.ok || !res.data?.data) { alert('급여 데이터 조회 실패'); return; }
  const html = buildPayslipHTML(res.data.data, period, companyName);
  openPrintWindow(html);
}

// ── 전체 급여명세서 일괄 인쇄 ─────────────────────────────────────────────────
export async function printAllPayslips(periodId, period, records, companyName) {
  if (!records || records.length === 0) { alert('인쇄할 급여 데이터가 없습니다.'); return; }
  let allHtml = '';
  for (const rec of records) {
    const res = await apiClient.get(`/api/admin/payroll/monthly/records/${rec.id}`);
    if (res.ok && res.data?.data) allHtml += buildPayslipHTML(res.data.data, period, companyName);
  }
  openPrintWindow(allHtml);
}

/** 인쇄 전용 창 열기 */
function openPrintWindow(bodyHtml) {
  const w = window.open('', '_blank', 'width=800,height=900');
  if (!w) { alert('팝업이 차단되었습니다. 팝업 허용 후 다시 시도하세요.'); return; }
  w.document.write(`<!DOCTYPE html><html lang="ko"><head>
<meta charset="utf-8"><title>급여명세서</title>
<style>
/* ── 전역 리셋 ── */
*{box-sizing:border-box;margin:0;padding:0}
body{
  font-family:'Pretendard Variable','Pretendard','Apple SD Gothic Neo','Malgun Gothic',sans-serif;
  font-size:11px;color:#1a1a1a;line-height:1.5;
  -webkit-print-color-adjust:exact;print-color-adjust:exact;
}
@media print{body{margin:0}@page{size:A4;margin:14mm 16mm}}

/* ── 명세서 컨테이너 ── */
.slip{
  page-break-after:always;max-width:580px;margin:24px auto;padding:0;
}

/* ── 상단 액센트 바 ── */
.accent-bar{height:4px;background:#0f1b35;border-radius:2px 2px 0 0}

/* ── 헤더 ── */
header{padding:20px 0 16px;text-align:center;border-bottom:1px solid #e5e7eb}
.co{font-size:10px;color:#9ca3af;letter-spacing:2px;font-weight:500;margin-bottom:4px}
h1{font-size:20px;font-weight:800;color:#0f1b35;letter-spacing:6px;margin:0}
.period{font-size:10px;color:#6b7280;margin-top:6px}

/* ── 직원 정보 테이블 ── */
.info{width:100%;border-collapse:collapse;margin-top:16px}
.info th{
  width:70px;padding:6px 10px;font-size:10px;color:#6b7280;font-weight:600;
  text-align:left;background:#fafafa;border:1px solid #f0f0f0;
}
.info td{padding:6px 10px;font-size:11px;border:1px solid #f0f0f0}

/* ── 섹션 제목 ── */
h2{
  font-size:9px;font-weight:700;color:#9ca3af;letter-spacing:2px;
  text-transform:uppercase;margin:18px 0 8px;padding:0;
}

/* ── 근무 정보 그리드 ── */
.stats{
  display:grid;grid-template-columns:repeat(4,1fr);gap:0;
  border:1px solid #f0f0f0;border-radius:6px;overflow:hidden;
}
.stats dl{
  display:flex;flex-direction:column;align-items:center;
  padding:8px 4px;border-right:1px solid #f0f0f0;border-bottom:1px solid #f0f0f0;
}
.stats dl:nth-child(4n){border-right:none}
.stats dl:nth-child(n+5){border-bottom:none}
.stats dt{font-size:8px;color:#9ca3af;font-weight:500;margin-bottom:2px}
.stats dd{font-size:11px;font-weight:700;color:#1a1a1a}

/* ── 통상임금 ── */
.wage{
  display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;
  margin-top:12px;padding:8px 12px;background:#f8f9fc;border-radius:4px;
  font-size:10px;
}
.wage-lbl{font-weight:700;color:#4338ca;font-size:10px}
.wage-val{font-weight:700;color:#1a1a1a;font-size:12px}
.wage-rate{color:#9ca3af;font-size:9px;margin-left:auto}

/* ── 지급/공제 테이블 ── */
.pay{width:100%;border-collapse:collapse;margin-top:16px;font-size:11px}
.pay .cat th{
  padding:7px 8px;font-size:10px;font-weight:700;text-align:center;
  border:1px solid #e5e7eb;
}
.cat .earn{background:#f0fdf4;color:#15803d}
.cat .ded{background:#fef2f2;color:#b91c1c}
.pay .sub th{
  padding:4px 8px;font-size:8px;font-weight:600;color:#9ca3af;
  text-align:left;border:1px solid #f0f0f0;background:#fafafa;
}
.pay tbody td{padding:5px 8px;border-bottom:1px solid #f5f5f5}
.pay .r{text-align:right;font-variant-numeric:tabular-nums}
.pay .sub .r{text-align:right}
.tf{display:inline-block;font-size:8px;color:#0891b2;margin-right:3px}
.pay tfoot td{
  padding:7px 8px;font-weight:800;font-size:11px;
  border-top:2px solid #e5e7eb;
}
.earn-t{color:#15803d}
.ded-t{color:#b91c1c}
.neg{color:#b91c1c}

/* ── 산출 내역 ── */
.formula{margin-top:16px}
.formula h2{margin-bottom:6px}
.f-block{
  padding:6px 0;border-bottom:1px dashed #e5e7eb;
  display:flex;flex-wrap:wrap;align-items:baseline;gap:4px 8px;
  font-size:10px;color:#374151;
}
.f-block:last-child{border-bottom:none}
.f-name{font-weight:700;color:#1a1a1a;min-width:64px}
.f-expr{color:#6b7280}

/* ── 합계 요약 ── */
.summary{margin-top:18px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden}
.summary-top{
  display:flex;justify-content:space-between;padding:8px 16px;
  font-size:10px;color:#6b7280;background:#fafafa;border-bottom:1px solid #e5e7eb;
}
.summary-top b{color:#374151;margin-left:4px}
.summary-net{
  display:flex;justify-content:space-between;align-items:center;
  padding:14px 20px;background:#0f1b35;
}
.summary-net span{color:rgba(255,255,255,.65);font-size:12px;font-weight:600}
.net{font-size:22px!important;font-weight:900!important;color:#fff!important;letter-spacing:1px;font-variant-numeric:tabular-nums}

/* ── 푸터 ── */
footer{margin-top:16px;text-align:center;font-size:8px;color:#d1d5db;letter-spacing:.3px}
</style>
</head><body>${bodyHtml}</body></html>`);
  w.document.close();
  setTimeout(() => { w.print(); }, 300);
}
